/**
 * Verify-bounded quest runner ("Ralph Loop").
 *
 * Iterates up to `termination.maxIterations`. Each iteration:
 *   1. Spawns a worker agent and runs `record.prompt`.
 *   2. Runs every `termination.verifyChecks` shell command in `record.cwd`.
 *      If all pass → iteration succeeds and the quest terminates as
 *      `succeeded`. If any fails → iteration is marked failed and the loop
 *      continues to the next iteration (the worker tries again).
 *   3. If `termination.verifyPrompt` is set → currently NOT supported in v1
 *      (a verifier agent + structured response would land in a follow-up).
 *      The runner records the limitation and treats verify-checks as the
 *      sole pass/fail signal.
 *
 * After `maxIterations` without a pass → quest is marked `failed`.
 *
 * Cards are appended dynamically as iterations run (quests of kind "ralph"
 * start with `cards: []`). Each card represents one worker attempt and carries
 * the verify-check outcome on `card.verify`.
 */

import { promisify } from "node:util";
import { execFile } from "node:child_process";
import { appendFile, mkdir, writeFile } from "node:fs/promises";
import path from "node:path";
import type { Logger } from "pino";
import type { AgentSessionConfig } from "../agent/agent-sdk-types.js";
import { getProviderFullAccessModeId } from "../agent/provider-manifest.js";
import { QuestCardSchema, type QuestCard, type QuestRecord } from "./types.js";
import type { QuestRunner, QuestService } from "./service.js";
import { buildAgentOwnerOptions } from "./agent-owner-options.js";

const execFileAsync = promisify(execFile);
const MAX_VERIFY_OUTPUT_BYTES = 64 * 1024;

function nowIso(): string {
  return new Date().toISOString();
}

function platformShell(): { command: string; flag: string[] } {
  if (process.platform === "win32") {
    return { command: "cmd.exe", flag: ["/d", "/s", "/c"] };
  }
  return { command: "/bin/sh", flag: ["-c"] };
}

interface VerifyCheckOutcome {
  command: string;
  passed: boolean;
  exitCode: number;
  output: string;
}

async function runVerifyCheck(args: { cwd: string; command: string }): Promise<VerifyCheckOutcome> {
  const shell = platformShell();
  try {
    const result = await execFileAsync(shell.command, [...shell.flag, args.command], {
      cwd: args.cwd,
      maxBuffer: MAX_VERIFY_OUTPUT_BYTES,
    });
    const out = [result.stdout?.trim(), result.stderr?.trim()].filter(Boolean).join("\n");
    return { command: args.command, passed: true, exitCode: 0, output: out };
  } catch (err) {
    const e = err as Error & { code?: number | string; stdout?: string; stderr?: string };
    const out = [e.stdout?.trim?.(), e.stderr?.trim?.()].filter(Boolean).join("\n");
    return {
      command: args.command,
      passed: false,
      exitCode: typeof e.code === "number" ? e.code : 1,
      output: out || e.message,
    };
  }
}

function buildWorkerConfig(record: QuestRecord, iteration: number): AgentSessionConfig {
  const modeId = getProviderFullAccessModeId(record.defaultProvider) ?? undefined;
  return {
    provider: record.defaultProvider,
    cwd: record.cwd,
    model: record.defaultModel ?? undefined,
    modeId,
    title: `quest ${record.id} ralph #${iteration}`,
    internal: true,
  };
}

function buildRalphIterationPrompt(args: {
  basePrompt: string;
  completionPromise: string;
  verifyChecks: readonly string[];
}): string {
  const checks =
    args.verifyChecks.length > 0 ? args.verifyChecks.map((c) => `- ${c}`).join("\n") : "- (none)";
  return [
    args.basePrompt,
    "",
    "Ralph loop completion contract (strict):",
    `- You may only declare completion when all verify checks pass:`,
    checks,
    `- When complete, output this exact token verbatim: ${args.completionPromise}`,
    "- If not complete yet, continue making changes and do not output the token.",
  ].join("\n");
}

function resolveFinalTextFromRun(run: {
  timeline: ReadonlyArray<unknown>;
  finalText: string;
}): string {
  if (run.finalText.trim()) {
    return run.finalText;
  }
  for (let i = run.timeline.length - 1; i >= 0; i -= 1) {
    const item = run.timeline[i];
    if (
      item &&
      typeof item === "object" &&
      "type" in item &&
      (item as { type: string }).type === "assistant_message" &&
      "text" in item &&
      typeof (item as { text: string }).text === "string"
    ) {
      const text = (item as { text: string }).text;
      if (text.trim()) {
        return text;
      }
    }
  }
  return "";
}

function runContainsPromise(
  run: { timeline: ReadonlyArray<unknown>; finalText: string },
  completionPromise: string,
): boolean {
  const finalText = resolveFinalTextFromRun(run);
  return finalText.includes(completionPromise);
}

interface RalphStatePaths {
  dir: string;
  logPath: string;
}

function resolveRalphStatePaths(cwd: string): RalphStatePaths {
  const dir = path.join(cwd, ".hiveminds", "ralph loop");
  return {
    dir,
    logPath: "",
  };
}

async function initializeRalphState(args: {
  record: QuestRecord;
  verifyChecks: readonly string[];
  completionPromise: string;
  maxIterations: number;
}): Promise<RalphStatePaths> {
  const base = resolveRalphStatePaths(args.record.cwd);
  const paths: RalphStatePaths = {
    dir: base.dir,
    logPath: path.join(base.dir, `ralphloop_${args.record.id}.md`),
  };
  await mkdir(paths.dir, { recursive: true });
  await writeFile(
    paths.logPath,
    [
      `# Ralph Loop ${args.record.id}`,
      "",
      `- createdAt: ${args.record.createdAt}`,
      `- prompt: ${args.record.prompt}`,
      `- completionPromise: ${args.completionPromise}`,
      `- maxIterations: ${args.maxIterations}`,
      "",
      "## Verify Checks",
      ...(args.verifyChecks.length > 0
        ? args.verifyChecks.map((check) => `- ${check}`)
        : ["- (none)"]),
      "",
      "## Progress",
    ].join("\n") + "\n",
    "utf8",
  );
  await appendFile(paths.logPath, `- [${nowIso()}] started\n`, "utf8");
  return paths;
}

async function appendRalphProgress(paths: RalphStatePaths, message: string): Promise<void> {
  await appendFile(paths.logPath, `- [${nowIso()}] ${message}\n`, "utf8");
}

function newCard(index: number): QuestCard {
  return QuestCardSchema.parse({
    index,
    role: null,
    model: null,
    prompt: null,
    wiring: { inputs: "user-prompt", outputs: "none" },
    status: "queued",
    agentId: null,
    startedAt: null,
    completedAt: null,
    verify: {
      verifierAgentId: null,
      passed: null,
      reason: null,
      startedAt: null,
      completedAt: null,
    },
  });
}

export interface RalphRunnerOptions {
  logger: Logger;
}

export class RalphRunner implements QuestRunner {
  private readonly logger: Logger;

  constructor(options: RalphRunnerOptions) {
    this.logger = options.logger.child({ module: "quest-runner-ralph" });
  }

  async execute(args: {
    questId: string;
    signal: AbortSignal;
    service: QuestService;
  }): Promise<void> {
    const { questId, signal, service } = args;
    const initial = service.getRecord(questId);
    if (!initial) {
      throw new Error(`Quest "${questId}" not found`);
    }
    if (initial.kind !== "ralph") {
      throw new Error(`RalphRunner cannot execute quest of kind "${initial.kind}"`);
    }
    if (initial.termination.type !== "verify") {
      throw new Error(`RalphRunner expects termination.type === "verify"`);
    }
    const { verifyPrompt, verifyChecks, completionPromise, maxIterations } = initial.termination;
    const statePaths = await initializeRalphState({
      record: initial,
      verifyChecks,
      completionPromise,
      maxIterations,
    });

    if (verifyPrompt) {
      this.logger.warn(
        { questId },
        "ralph-runner: verifyPrompt is set but verifier-agent support is not yet implemented in v1 — relying on verifyChecks only",
      );
    }
    if (verifyChecks.length === 0) {
      await service.markStatus(questId, "failed");
      throw new Error("Ralph quest requires at least one verifyCheck (verifier-agent path is v2)");
    }

    for (let iteration = 1; iteration <= maxIterations; iteration += 1) {
      const current = service.getRecord(questId);
      if (!current || current.status !== "running" || signal.aborted) {
        await service.markStatus(questId, "stopped");
        return;
      }

      const card = newCard(iteration);
      let recordWithCard = await service.appendCard(questId, card);
      await appendRalphProgress(statePaths, `iteration=${iteration} status=started`);

      // Spawn worker for this iteration.
      const startedAt = nowIso();
      const agent = await service.agentManager.createAgent(
        buildWorkerConfig(recordWithCard, iteration),
        undefined,
        buildAgentOwnerOptions(recordWithCard),
      );
      await service.updateCard(questId, iteration, {
        status: "running",
        agentId: agent.id,
        startedAt,
      });

      const onAbort = () => {
        void service.agentManager.cancelAgentRun(agent.id).catch(() => {});
      };
      signal.addEventListener("abort", onAbort, { once: true });

      let workerOk = false;
      let workerRun: Awaited<ReturnType<QuestService["agentManager"]["runAgent"]>> | null = null;
      try {
        const run = await service.agentManager.runAgent(
          agent.id,
          buildRalphIterationPrompt({
            basePrompt: recordWithCard.prompt,
            completionPromise,
            verifyChecks,
          }),
        );
        if (run.canceled) {
          await appendRalphProgress(
            statePaths,
            `iteration=${iteration} status=stopped reason=canceled`,
          );
          await service.updateCard(questId, iteration, {
            status: "stopped",
            completedAt: nowIso(),
          });
          await service.markStatus(questId, "stopped");
          return;
        }
        workerRun = run;
        workerOk = true;
      } catch (err) {
        await appendRalphProgress(
          statePaths,
          `iteration=${iteration} status=failed reason=worker_error`,
        );
        this.logger.error({ err, questId, iteration }, "ralph-runner: worker errored");
        await service.updateCard(questId, iteration, {
          status: "failed",
          completedAt: nowIso(),
        });
      } finally {
        signal.removeEventListener("abort", onAbort);
        try {
          await service.agentManager.closeAgent(agent.id);
        } catch {
          // ignore
        }
      }

      if (!workerOk) {
        // Worker failed entirely — proceed to next iteration without verifying.
        continue;
      }

      // Stop-hook style gate: no completion token means forced continuation.
      if (!workerRun || !runContainsPromise(workerRun, completionPromise)) {
        await appendRalphProgress(
          statePaths,
          `iteration=${iteration} status=failed reason=missing_completion_promise`,
        );
        await service.updateCard(questId, iteration, {
          status: "failed",
          completedAt: nowIso(),
          verify: {
            verifierAgentId: null,
            passed: false,
            reason: `missing completion promise: ${completionPromise}`,
            startedAt: null,
            completedAt: nowIso(),
          },
        });
        continue;
      }

      // Run verify-checks. If all pass and promise exists → quest succeeds.
      const verifyStartedAt = nowIso();
      let allPassed = true;
      const reasons: string[] = [];
      for (const command of verifyChecks) {
        if (signal.aborted) {
          await service.markStatus(questId, "stopped");
          return;
        }
        const outcome = await runVerifyCheck({
          cwd: recordWithCard.cwd,
          command,
        });
        if (!outcome.passed) {
          allPassed = false;
          reasons.push(`exit ${outcome.exitCode}: ${command}`);
          break;
        }
      }
      const verifyCompletedAt = nowIso();

      await service.updateCard(questId, iteration, {
        status: allPassed ? "succeeded" : "failed",
        completedAt: nowIso(),
        verify: {
          verifierAgentId: null,
          passed: allPassed,
          reason: allPassed ? "verify-checks passed" : reasons.join(" | "),
          startedAt: verifyStartedAt,
          completedAt: verifyCompletedAt,
        },
      });

      if (allPassed) {
        await appendRalphProgress(statePaths, `iteration=${iteration} status=succeeded`);
        await service.markStatus(questId, "succeeded");
        return;
      }
      await appendRalphProgress(
        statePaths,
        `iteration=${iteration} status=failed reason=verify_checks`,
      );
      // Otherwise, loop continues to the next iteration.
      recordWithCard = service.getRecord(questId) ?? recordWithCard;
    }

    // maxIterations exhausted without a pass.
    await appendRalphProgress(statePaths, "quest status=failed reason=max_iterations_exhausted");
    await service.markStatus(questId, "failed");
  }
}
